'use client'

import React, { useEffect, useState, useCallback, useMemo } from "react"
import { motion } from "framer-motion"
import HighlightPost from "@/components/news/HighlightPost"
import PopularDestinationsCTA from "@/components/news/PopularDestinationsCTA"
import DropDown from "./DropDown"
import Card from "./Card"
import { useRouter, useSearchParams } from "next/navigation"
import { getContentsByType } from "actions/content"
import useSWR from "swr"
import { getContentTypes } from "actions/common"
import CardSkeleton from "./CardSekelon"
import HighlightPostSkeleton from "./HighlightPostSkeleton"
import { Skeleton } from "@nextui-org/react"

interface IProps {
  locale: string
}

interface Blog {
  id: string
  [key: string]: any
}

function NewsList(props: IProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const categoryParam = searchParams.get("category") || "Travel Guide"

  const [page, setPage] = useState(1)
  const [allBlogs, setAllBlogs] = useState<Blog[]>([])
  const [isLoadMoreEnable, setIsLoadMoreEnable] = useState(true)

  const contentTypesData = useSWR("contentTypes", getContentTypes, { keepPreviousData: true })

  // fetch blogs
  const { data: blogs, error, isLoading } = useSWR(
    [`blogs-${categoryParam}-${page}`, props.locale, categoryParam, page],
    async () => await getContentsByType({
      page,
      sort: "NEWEST",
      type: categoryParam,
      take: 6,
      locale: props.locale,
    }),
    { keepPreviousData: true }
  )

  // reset when category changes
  useEffect(() => {
    if (!contentTypesData.isLoading && categoryParam) {
      setPage(1);
      setAllBlogs([]);
    }
  }, [categoryParam])

  // update list when blogs fetched
  useEffect(() => {
    if (blogs && !isLoading) {
      if (page === 1) {
        setAllBlogs(blogs.results || [])
      } else {
        setAllBlogs(prev => [...prev, ...(blogs.results || [])])
      }
      setIsLoadMoreEnable(blogs.currentPage < blogs.totalPages)
    }
  }, [blogs, page, isLoading])

  const handleSelectionChange = useCallback((keys: Set<string>) => {
    const newCategory = Array.from(keys)[0]
    router.push(`?category=${newCategory}`)
  }, [router])

  useEffect(() => {
    if (!contentTypesData.isLoading && categoryParam) {
      const isAllowed = contentTypesData.data?.data?.some(item => item.type === categoryParam)
      if (!isAllowed) {
        router.push(`?category=${contentTypesData.data?.data?.[0]?.type ?? ""}`)
      }
    }
  }, [categoryParam, contentTypesData.data, contentTypesData.isLoading, router])

  const loadMore = useCallback(() => {
    setPage(prev => prev + 1)
  }, [])

  const renderBlogItem = useCallback((blog: Blog, index: number, isNewItem: boolean) => {
    const BlogComponent = index === 0 ? HighlightPost : Card
    // @ts-ignore
    const content = <BlogComponent key={blog.id} {...blog} />
    if (isNewItem) {
      return (
        <motion.div
          key={blog.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {content}
        </motion.div>
      )
    }
    return content
  }, [])

  const memoizedPopularDestinationsCTA = useMemo(() => (
    <PopularDestinationsCTA
      category="Travel guide"
      title="It's A Big World Out There, Go Explore!"
      desc="Explore the world with TravelThru. Uncover new adventures, cultures, and experiences as you travel far and wide. Join us on your next journey and discover the wonders of the world."
      href="#popular-destinations"
    />
  ), [])

  return (
    <div className="bg-black w-full flex items-center flex-col justify-center pb-[77px] px-[24px] xl:px-[0px]">
      <div className="max-w-screen-xl w-full">
        {contentTypesData.isLoading ? (
          <Skeleton className="flex rounded-full w-[120px] h-[40px] mt-2 !bg-[#7D7D7D] before:!duration-600 before:!bg-opacity-10" />
        ) : (
          <DropDown
            selectedKeys={new Set([categoryParam])}
            onSelectionChange={handleSelectionChange}
            dropdownItems={contentTypesData.data?.data ?? []}
          />
        )}
      </div>

      <div className="max-w-screen-xl w-full grid lg:grid-cols-3 grid-cols-2 mt-[26px] gap-y-[45px] lg:gap-y-[80px] lg:gap-x-[32px] gap-x-[12px]">
        {isLoading && page === 1 ? (
          <>
            <div className="col-span-3">
              <HighlightPostSkeleton />
            </div>
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : error ? (
          <div className="col-span-3 text-white">Error loading blogs: {error.message}</div>
        ) : (
          <>
            {allBlogs.map((blog, index) => (
              <div key={blog.id} className={index === 0 ? "lg:col-span-3 col-span-2" : ""}>
                {renderBlogItem(blog, index, index >= allBlogs.length - (blogs?.results?.length || 0))}
              </div>
            ))}
          </>
        )}
        {memoizedPopularDestinationsCTA}
        {isLoading && page > 1 && (
          <>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </>
        )}
      </div>

      {isLoadMoreEnable && !isLoading && (
        <button
          onClick={loadMore}
          className="!text-black !no-underline bg-white rounded-[10px] text-[16px] font-bold tracking-[-0.48x] leading-[1.2] px-[32px] py-[14px] mt-[80px]"
          aria-label="Load more blog posts"
        >
          Load More
        </button>
      )}
    </div>
  )
}

export default NewsList
